/**
 * ProtoUtils.ts
 * makeAutoObservable doesn't seem to work for protos.
 */
import { Base64 } from "js-base64";
import { makeObservable, observable, toJS } from "mobx";
import spacetime from "spacetime";
import deepDiff from "deep-diff";
import { UserState } from "tsproto/aware_proto/proto/AwareEngine";
import { AgentInfo, EmailIndexedUser, Execution, ExecutionData, ExecutionData_EventType, Firing, GroupInfo, IntentRequest, Membership, PetriNetState, PlaceData, UserInfo } from "tsproto/aware_proto/proto/AliveNet";
import { ActivityStatusConditionParams, AgendaRequest, AutomationParams, BoundedTimeConditionParams, ControlLightActionParams, ControlThermostatActionParams, DateConditionParams, EmailFromSenderConditionParams, EmailMessage, ETAQuery, EventAtPlaceConditionParams, EventConditionParams, EventPref, EventQuery, ExactTimeConditionParams, GapiToken, GoalProgress, HabitQuery, MapPin, MapRoute, Modules, PlaceMatch, RecapView, RecurrenceParams, RewardEarned, SonosGroup, SonosHousehold, SonosPlayAudioClipRequest, SonosPlaybackRequest, SonosPlayer, SonosPlayFavoriteRequest, SonosState, TellUserActionParams, Timer, Timers, UserGoal, UserMood, UserReview } from "tsproto/aware_proto/proto/Modules";
import { BoolValue, BytesValue, DoubleValue, FloatValue, Int32Value, Int64Value, StringValue, UInt32Value, UInt64Value } from "tsproto/google/protobuf/wrappers";
import { Any } from "tsproto/google/protobuf/any";
import { Timestamp } from "tsproto/google/protobuf/timestamp";
import { EventConstraints } from "tsproto/aware_proto/proto/Event";
import { Intent, SystemMessage, TextSnippet, UserInput, UserResponseOption } from "tsproto/aware_proto/proto/Interaction";
import { Duration } from "tsproto/google/protobuf/duration";
import { Empty } from "tsproto/google/protobuf/empty";
import Log, { LogColor } from "./Log";
import PetriNet from "./control/PetriNet";
export default class ProtoUtils {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    static typeRegistry = [
        // google/protobuf
        Timestamp, Duration, Empty,
        DoubleValue, FloatValue, Int64Value, UInt64Value, Int32Value, UInt32Value, BoolValue,
        StringValue, BytesValue,
        // AliveNet
        Execution, ExecutionData, Firing, PetriNetState, PlaceData, GapiToken,
        // Interaction
        Intent, IntentRequest, UserInput, UserResponseOption, SystemMessage, TextSnippet,
        // Modules
        AgendaRequest, Timer, Timers, EventConstraints, EventPref, RecapView, HabitQuery, EventQuery,
        ETAQuery, MapPin, MapRoute, EmailMessage, UserGoal, GoalProgress, RewardEarned, PlaceMatch,
        UserMood, UserReview,
        // Sonos
        SonosState, SonosHousehold, SonosGroup, SonosPlayer, SonosPlaybackRequest,
        SonosPlayFavoriteRequest, SonosPlayAudioClipRequest,
        // Automations
        TellUserActionParams, ControlLightActionParams, ControlThermostatActionParams,
        ExactTimeConditionParams, DateConditionParams, BoundedTimeConditionParams,
        EventAtPlaceConditionParams, EventConditionParams, EmailFromSenderConditionParams,
        ActivityStatusConditionParams, AutomationParams, RecurrenceParams
    ];
    // Used for deserializing JSON with Any protos
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    static registerType(messageType) {
        if (!this.typeRegistry.includes(messageType)) {
            ProtoUtils.typeRegistry.push(messageType);
        }
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    static messageType(arg) {
        if (!arg)
            return undefined;
        if (typeof arg === "string") {
            return ProtoUtils.typeRegistry.find(type => arg.endsWith(type.typeName));
        }
        return ProtoUtils.typeRegistry.find(type => type.is(arg));
    }
    // eslint-disable-next-line @typescript-eslint/ban-types
    static protoFromBase64String(base64String, messageType) {
        const uint8Array = Base64.toUint8Array(base64String);
        return messageType.fromBinary(uint8Array);
    }
    // eslint-disable-next-line @typescript-eslint/ban-types
    static protoToBase64String(message, messageType) {
        const uint8Array = messageType.toBinary(message);
        return Base64.fromUint8Array(uint8Array);
    }
    // Use when passing data across the Swift/WKWebView boundary, which will pass a JSON or base 64 string instead.
    // eslint-disable-next-line @typescript-eslint/ban-types
    static asMessageType(message, messageType) {
        if (typeof message === "string") {
            if (Base64.isValid(message)) {
                return ProtoUtils.protoFromBase64String(message, messageType);
            }
            return messageType.fromJsonString(message);
        }
        return message;
    }
    static point3ToString(point) {
        return `[${point.x.toFixed(3)}, ${point.y.toFixed(3)}, ${point.z.toFixed(3)}]`;
    }
    static observableUserInfo(userInfo) {
        return ProtoUtils.observable(userInfo, UserInfo);
    }
    static observableAgentInfo(agentInfo) {
        return ProtoUtils.observable(agentInfo, AgentInfo);
    }
    static observableMembership(membership) {
        return ProtoUtils.observable(membership, Membership);
    }
    static observableGroupInfo(groupInfo) {
        return ProtoUtils.observable(groupInfo, GroupInfo);
    }
    static observableModules(modules) {
        return ProtoUtils.observable(modules, Modules);
    }
    static observableEmailIndexedUser(emailIndexedUser) {
        return ProtoUtils.observable(emailIndexedUser, EmailIndexedUser);
    }
    static observableUserState(userState) {
        return ProtoUtils.observable(userState, UserState);
    }
    static observableGapiToken(gapiToken) {
        return ProtoUtils.observable(gapiToken, GapiToken);
    }
    // eslint-disable-next-line @typescript-eslint/ban-types, @typescript-eslint/no-explicit-any
    static observable(object, messageType) {
        if (!object)
            return object;
        const params = {};
        Object.keys(object).forEach(fieldName => {
            params[fieldName] = observable;
        });
        const clone = messageType.clone(object);
        return makeObservable(clone, params);
    }
    static stringValue(string) {
        const proto = StringValue.create();
        proto.value = string;
        return proto;
    }
    static packString(string) {
        return Any.pack(ProtoUtils.stringValue(string), StringValue);
    }
    static unpackString(anyProto) {
        return Any.unpack(anyProto, StringValue)?.value;
    }
    static doubleValue(value) {
        const proto = DoubleValue.create();
        proto.value = value;
        return proto;
    }
    static prettify(obj) {
        try {
            obj = toJS(obj);
            obj = Object.assign({}, obj);
            if (ProtoUtils.isTimestamp(obj)) {
                const timestamp = obj;
                return ProtoUtils.toISOStringWithNanos(timestamp);
            }
            else if (Empty.is(obj)) {
                return "<google.protobuf.Empty>";
            }
            else if (Any.is(obj)) {
                const any = obj;
                const messageType = ProtoUtils.messageType(any.typeUrl);
                if (messageType) {
                    const unpacked = Any.unpack(any, messageType);
                    return ProtoUtils.prettify(unpacked);
                }
                else {
                    Log.warn(ProtoUtils.name, `Unknown message type ${any.typeUrl}`);
                    return any;
                }
            }
            else if (typeof obj === "object") {
                for (const key in obj) {
                    if (typeof obj[key] === "object" &&
                        !Object.getOwnPropertyDescriptor(obj, key)["get"]) {
                        obj[key] = ProtoUtils.prettify(obj[key]);
                    }
                }
            }
        }
        catch (error) {
            Log.error(ProtoUtils.name, "Error while prettifying: " + error);
        }
        return obj;
    }
    static toSpacetime(timestamp, timezone) {
        if (!timestamp)
            return undefined;
        const date = Timestamp.toDate(timestamp);
        return spacetime(date.getTime(), timezone);
    }
    static toTimestamp(st) {
        return Timestamp.fromDate(st.toNativeDate());
    }
    static timeOrigin;
    /**
     * Gets the current time as a protobuf Timestamp with microsecond precision.
     */
    static now() {
        if (!performance) {
            Log.warn(ProtoUtils.name, "performance is not defined");
            return Timestamp.now();
        }
        else {
            if (!ProtoUtils.timeOrigin) {
                const timeOrigin = performance.timeOrigin;
                if ((typeof timeOrigin) === "number") {
                    ProtoUtils.timeOrigin = timeOrigin;
                }
                else {
                    // This is an issue with Jest mock timers; only performance.now() gets mocked
                    this.timeOrigin = new Date().getTime();
                }
            }
            const performanceMs = ProtoUtils.timeOrigin + performance.now();
            const wallClockMs = new Date().getTime();
            const error = wallClockMs - performanceMs;
            if (Math.abs(error) > 1) {
                Log.debug(ProtoUtils.name, `Adjusting time origin (error was ${error})`);
                ProtoUtils.timeOrigin = new Date().getTime() - performance.now();
                return Timestamp.now();
            }
            return ProtoUtils.createTimestamp(performanceMs);
        }
    }
    /**
     * Gets the time value in units of milliseconds. Any additional precision available will appear
     * after the decimal point.
     */
    static getTimeMs(timestamp) {
        return timestamp.seconds * 1e3 + timestamp.nanos / 1e6;
    }
    /**
     * Convert to Date while preserving microsecond precision.
     */
    static toDate(timestamp) {
        const ms = ProtoUtils.getTimeMs(timestamp);
        return new Date(ms);
    }
    static createTimestamp(ms) {
        const s = ms / 1e3;
        const nanos = (s % 1) * 1e9;
        return Timestamp.create({
            seconds: Math.trunc(s),
            nanos: Math.round(nanos)
        });
    }
    // For some reason Timestamp.is() doesn't always work after copies
    static isTimestamp(obj) {
        const timestamp = obj;
        if (timestamp?.nanos !== undefined && timestamp?.seconds !== undefined) {
            return true;
        }
        return false;
    }
    /**
     * Gets an ISO-like formatted string for the timestamp except with nanosecond rather than
     * millisecond precision.
     */
    static toISOStringWithNanos(timestamp) {
        let string = Timestamp.toDate(timestamp).toISOString();
        const nanoStr = String(timestamp.nanos).padStart(9, "0");
        string = string.replace(/.\d{3,}(?=Z$)/, `.${nanoStr}`);
        return string;
    }
    static toISOString(timestamp) {
        return Timestamp.toDate(timestamp).toISOString();
    }
    static printTimeErrorPeriodically(intervalMs) {
        const startTime = new Date().getTime();
        return setInterval(() => {
            const performanceNow = performance.timeOrigin + performance.now();
            const protoNow = ProtoUtils.getTimeMs(ProtoUtils.now());
            const wallClockNow = new Date().getTime();
            const performanceError = wallClockNow - performanceNow;
            const correctedError = wallClockNow - protoNow;
            const elapsed = wallClockNow - startTime;
            Log.info(ProtoUtils.name, `time corrected error: ${correctedError}. ` +
                `performance error: ${performanceError}. ` +
                `total elapsed: ${elapsed}`);
        }, intervalMs);
    }
    static enumValues(enumObj) {
        return Object.keys(enumObj).filter(v => isNaN(Number(v)));
    }
    static enumLength(enumObj) {
        return Object.keys(enumObj).length / 2;
    }
    static printEmailMessageWithoutContent(emailMessage) {
        console.log(Timestamp.toDate(emailMessage.timestamp));
        const copy = EmailMessage.clone(emailMessage);
        copy.contentPlain = "<redacted>";
        copy.contentHtml = "<redacted>";
        console.log(copy);
    }
    static getExecutionSummaryString(executionData) {
        const agentUid = executionData.agentUid;
        const surfaceUid = executionData.surfaceUid;
        const eventTypeStr = ExecutionData_EventType[executionData.eventType];
        const timeStr = ProtoUtils.toISOStringWithNanos(executionData.timestamp);
        let str = "";
        str += `${timeStr} ${agentUid} ${eventTypeStr} on ${surfaceUid}\n`;
        str += `  ${executionData.message}\n`;
        // Print execution diffs
        const ref = executionData.executions;
        if (executionData.firing) {
            str += "Firing:\n";
            if (executionData.eventType === ExecutionData_EventType.ClientFiringRejected) {
                const prev = executionData.firing.prevExecutions;
                const next = executionData.firing.nextExecutions;
                str += `${"-".repeat(5)} Diff of prev from ref (state mismatch): ${"-".repeat(5)}\n`;
                str += this.getExecutionDiffsString(ref, prev);
                str += `${"-".repeat(5)} Diff of next from prev (attempted firing): ${"-".repeat(5)}\n`;
                str += this.getExecutionDiffsString(prev, next);
            }
            else {
                const prev = executionData.firing.prevExecutions;
                str += this.getExecutionDiffsString(prev, ref);
            }
        }
        if (executionData.reset) {
            str += "Reset:\n";
            const prev = executionData.reset.prevExecutions;
            str += this.getExecutionDiffsString(prev, ref);
        }
        // Print execution snapshot
        str += "Executions:\n";
        str += `timestamp${" ".repeat(21)}\t lastChangedSurfaceUid${" ".repeat(15)}\t tokens\t controllerType\n`;
        const controllerUids = Object.keys(executionData.executions);
        controllerUids.sort((a, b) => {
            const ta = ProtoUtils.getTimeMs(executionData.executions[a].timestamp);
            const tb = ProtoUtils.getTimeMs(executionData.executions[b].timestamp);
            return tb - ta;
        });
        const markedControllerUids = [];
        for (const controllerUid of controllerUids) {
            const execution = executionData.executions[controllerUid];
            const tLastChanged = ProtoUtils.toISOStringWithNanos(execution.timestamp);
            const nTokens = PetriNet.countTokens(execution.petriNetState?.marking);
            if (nTokens > 0) {
                markedControllerUids.push(controllerUid);
            }
            const nTokensStr = `${nTokens}${" ".repeat(6 - nTokens.toString().length)}`;
            str += `${tLastChanged} \t ${execution.lastChangedSurfaceUid} \t ${nTokensStr}` +
                `\t ${execution.controllerType}\n`;
        }
        for (const controllerUid of markedControllerUids) {
            str += `${controllerUid}:\n`;
            const state = executionData.executions[controllerUid].petriNetState;
            str += `${Log.inspect(ProtoUtils.prettify(state.marking))}\n`;
        }
        return str;
    }
    static getExecutionDiffsString(executions1, executions2, showMatches) {
        let str = "";
        const controllerUids = Array.from(new Set([...Object.keys(executions1), ...Object.keys(executions2)]));
        for (const controllerUid of controllerUids) {
            const execution1 = executions1[controllerUid];
            const execution2 = executions2[controllerUid];
            const state1 = execution1?.petriNetState || PetriNetState.create({
                petriNetId: controllerUid, marking: {}
            });
            const state2 = execution2?.petriNetState || PetriNetState.create({
                petriNetId: controllerUid, marking: {}
            });
            if (PetriNet.areStatesEqual(state1, state2)) {
                if (showMatches) {
                    str += `\u2713 ${controllerUid}` +
                        (!execution1 ? " (LHS undefined)" : "") +
                        (!execution2 ? " (RHS undefined)" : "") + "\n";
                }
                continue;
            }
            str += LogColor.yellow(`\u2192 ${controllerUid}\n`);
            const diffs = deepDiff(state1.marking, state2.marking);
            for (const diff of diffs) {
                if (diff.kind === "A") {
                    const diffArray = diff;
                    str += ` ARRAY CHANGE at index ${diffArray.index} to ${diff.path[0]}:\n`;
                    str += `${diffArray.item}\n`;
                }
                else if (diff.kind === "D") {
                    str += ` DELETE from ${diff.path[0]}:\n`;
                    str += `  ${Log.inspect(ProtoUtils.prettify(diff.lhs))}\n`;
                }
                else if (diff.kind === "E") {
                    str += ` EDIT to ${diff.path[0]}:\n`;
                    str += `  ${Log.inspect(ProtoUtils.prettify(diff.lhs))}\n`;
                    str += `  ${Log.inspect(ProtoUtils.prettify(diff.rhs))}\n`;
                }
                else if (diff.kind === "N") {
                    str += ` NEW to ${diff.path[0]}:\n`;
                    str += `  ${Log.inspect(ProtoUtils.prettify(diff.rhs))}\n`;
                }
                if (diff.path.length > 1) {
                    str += LogColor.yellow("Warning: diff path was longer than 1. Full diff:\n");
                    str += LogColor.yellow(Log.inspect(diff)) + "\n";
                }
            }
        }
        return str;
    }
}
